import inventoryText from "../inventoryI18n.js";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { NavLink } from "react-router-dom";
import axios from "axios";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
} from "@mui/x-data-grid";
import * as XLSX from "xlsx";

import ExcelSVG from "../ExcelSVG.svg";

import AuthContext from "../../context/AuthContext";
import { API_URL } from "../../components/api";
import "./Purchases.css";
import {
  PurchaseDocumentModal,
  PurchaseRefundPanel,
} from "./PurchaseDocuments";
import "../Suppliers/Suppliers.css";

const createRequestLine = () => ({
  row_id: `${Date.now()}-${Math.random()}`,
  product_id: "",
  quantity: "1",
});

const formatDate = (value) => {
  if (!value) {
    return "—";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const formatMoney = (value) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return "—";
  }

  return `$${parsed.toFixed(2)}`;
};

const statusLabel = (value) => {
  const normalized = String(
    value || ""
  ).toLowerCase();

  const translatedStatuses = {
    delivered: "inventory.Delivered",
    partially_delivered:
      "inventory.PartiallyDelivered",
    pending: "inventory.Pending",
  };

  if (translatedStatuses[normalized]) {
    return inventoryText(
      translatedStatuses[normalized]
    );
  }

  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
};

const requestNumber = (id) =>
  `PR-${String(id).padStart(6, "0")}`;

const purchaseOrderNumber = (id) =>
  `PO-${String(id).padStart(6, "0")}`;

const createReceiptIdempotencyKey = () => {
  if (
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
    .replace(/[xy]/g, (character) => {
      const randomValue =
        Math.floor(Math.random() * 16);
      const value =
        character === "x"
          ? randomValue
          : (randomValue & 0x3) | 0x8;

      return value.toString(16);
    });
};

const apiErrorMessage = (error, fallback) => {
  if (
    error.response &&
    error.response.data &&
    error.response.data.error
  ) {
    return error.response.data.error;
  }

  return fallback;
};

const getPurchaseHistoryEmployeeName = (employee) => {
  if (!employee) {
    return "";
  }

  return employee.full_name || employee.username || "";
};

const formatPurchaseHistoryDate = (value) => {
  if (!value) {
    return "";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString();
};

const getPurchaseHistoryMeta = (event) => {
  const entries = [];

  const addEntry = (label, value) => {
    if (
      value !== null &&
      value !== undefined &&
      value !== ""
    ) {
      entries.push({
        label,
        value,
      });
    }
  };

  addEntry(
    "Performed by",
    getPurchaseHistoryEmployeeName(event.actor)
  );

  if (event.purchase_request) {
    addEntry(
      "Request",
      event.purchase_request.request_number
    );

    addEntry(
      "Requested by",
      getPurchaseHistoryEmployeeName(
        event.purchase_request.initiator
      )
    );

    addEntry(
      "Approved by",
      getPurchaseHistoryEmployeeName(
        event.purchase_request.receiver
      )
    );
  }

  if (event.purchase_order) {
    addEntry(
      "Order",
      event.purchase_order.order_number
    );
  }

  if (event.goods_receipt) {
    addEntry(
      "Receipt",
      event.goods_receipt.receipt_number
    );
  }

  if (event.product) {
    addEntry("Product", event.product.name);
  }

  if (event.warehouse) {
    addEntry(
      "Warehouse",
      event.warehouse.warehouse_name
    );
  }

  if (event.amount_usd !== null) {
    addEntry("Amount", `$${event.amount_usd}`);
  }

  if (
    event.balance_before_usd !== null ||
    event.balance_after_usd !== null
  ) {
    addEntry(
      "Supplier balance",
      `$${event.balance_before_usd || "0.00"} → ` +
        `$${event.balance_after_usd || "0.00"}`
    );
  }

  if (event.quantity_delta !== null) {
    const quantityPrefix =
      event.quantity_delta > 0 ? "+" : "";

    let quantityValue =
      `${quantityPrefix}${event.quantity_delta}`;

    if (
      event.quantity_before !== null &&
      event.quantity_after !== null
    ) {
      quantityValue +=
        ` (${event.quantity_before} → ` +
        `${event.quantity_after})`;
    }

    addEntry("Quantity", quantityValue);
  }

  if (
    event.previous_status ||
    event.new_status
  ) {
    const statuses = [
      event.previous_status_label,
      event.new_status_label,
    ].filter(Boolean);

    addEntry("Status", statuses.join(" → "));
  }

  return entries;
};

function Purchases() {
  const { user } = useContext(AuthContext);

  const [permissions, setPermissions] =
    useState(null);
  const [requests, setRequests] = useState([]);
  const [searchInput, setStoredSearchInput] = useState(
    () => sessionStorage.getItem("searchPurchase") || ""
  );
  const setSearchInput = (value) => {
    sessionStorage.setItem("searchPurchase", value);
    setStoredSearchInput(value);
  };
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const [pageLoading, setPageLoading] =
    useState(true);
  const [pageRefreshing, setPageRefreshing] =
    useState(false);
  const [pageError, setPageError] = useState("");
  const [notice, setNotice] = useState("");

  const [showHistory, setShowHistory] =
    useState(false);
  const [historyRequest, setHistoryRequest] =
    useState(null);
  const [historyEvents, setHistoryEvents] =
    useState([]);
  const [historyLoading, setHistoryLoading] =
    useState(false);
  const [historyError, setHistoryError] =
    useState("");
  const [historyPage, setHistoryPage] =
    useState(1);
  const [historyPageSize] = useState(20);
  const [historyTotalPages, setHistoryTotalPages] =
    useState(0);
  const [historyCount, setHistoryCount] =
    useState(0);
  const latestHistoryFetchId = useRef(0);

  const [showRequestModal, setShowRequestModal] =
    useState(false);
  const [requestLines, setRequestLines] =
    useState([createRequestLine()]);
  const [requestError, setRequestError] =
    useState("");
  const [requestSubmitting, setRequestSubmitting] =
    useState(false);

  const [approvalRequest, setApprovalRequest] =
    useState(null);
  const [viewRequest, setViewRequest] =
    useState(null);
  const [approvalLines, setApprovalLines] =
    useState([]);
  const [approvalError, setApprovalError] =
    useState("");
  const [approvalSubmitting, setApprovalSubmitting] =
    useState(false);

  const [receiptOrder, setReceiptOrder] =
    useState(null);
  const [receiptWarehouseId, setReceiptWarehouseId] =
    useState("");
  const [receiptReference, setReceiptReference] =
    useState("");
  const [receiptNotes, setReceiptNotes] =
    useState("");
  const [receiptLines, setReceiptLines] =
    useState([]);
  const [receiptIdempotencyKey, setReceiptIdempotencyKey] =
    useState("");
  const [receiptError, setReceiptError] =
    useState("");
  const [receiptSubmitting, setReceiptSubmitting] =
    useState(false);

  const [inspectionRequest, setInspectionRequest] =
    useState(null);
  const [inspectionData, setInspectionData] =
    useState(null);
  const [inspectionLoading, setInspectionLoading] =
    useState(false);
  const [inspectionError, setInspectionError] =
    useState("");
  const [inspectionSaving, setInspectionSaving] =
    useState(false);
  const [inspectionNotesOnly, setInspectionNotesOnly] =
    useState(false);
  const [purchaseDocument, setPurchaseDocument] =
    useState(null);
  const [refundItemId, setRefundItemId] =
    useState(null);

  const loadPurchaseData = useCallback(
    async (quiet = false) => {
      if (quiet) {
        setPageRefreshing(true);
      } else {
        setPageLoading(true);
      }

      setPageError("");

      try {
        const storedTokens = JSON.parse(
          localStorage.getItem("authTokens")
        );

        const requestConfig = {
          headers: {
            Authorization:
              `Bearer ${storedTokens.access}`,
          },
        };

        const permissionResponse = await axios.get(
          `${API_URL}/inventory/purchases/permissions/`,
          requestConfig
        );

        const loadedPermissions =
          permissionResponse.data || {};

        setPermissions(loadedPermissions);

        const canCreate = Boolean(
          loadedPermissions.can_create_request
        );
        const canApprove = Boolean(
          loadedPermissions.can_approve_request
        );

        if (!canCreate && !canApprove) {
          setRequests([]);
          setProducts([]);
          setSuppliers([]);
          setWarehouses([]);
          return;
        }

        const requestsPromise = axios.get(
          `${API_URL}/inventory/purchases/requests/`,
          requestConfig
        );

        const productsPromise = canCreate
          ? axios.get(
              `${API_URL}/inventory/products/`,
              {
                ...requestConfig,
                params: {
                  username: user.username,
                },
              }
            )
          : Promise.resolve({
              data: [],
            });

        const suppliersPromise = canApprove
          ? axios.get(
              `${API_URL}/inventory/suppliers/`,
              {
                ...requestConfig,
                params: {
                  username: user.username,
                },
              }
            )
          : Promise.resolve({
              data: [],
            });

        const warehousesPromise = canApprove
          ? axios.get(
              `${API_URL}/inventory/warehouses/`,
              {
                ...requestConfig,
                params: {
                  username: user.username,
                },
              }
            )
          : Promise.resolve({
              data: [],
            });

        const [
          requestsResponse,
          productsResponse,
          suppliersResponse,
          warehousesResponse,
        ] = await Promise.all([
          requestsPromise,
          productsPromise,
          suppliersPromise,
          warehousesPromise,
        ]);

        const requestPayload =
          requestsResponse.data || {};

        setRequests(
          Array.isArray(requestPayload.results)
            ? requestPayload.results
            : []
        );

        setProducts(
          Array.isArray(productsResponse.data)
            ? productsResponse.data
            : []
        );

        setSuppliers(
          Array.isArray(suppliersResponse.data)
            ? suppliersResponse.data
            : []
        );

        setWarehouses(
          Array.isArray(warehousesResponse.data)
            ? warehousesResponse.data
            : []
        );
      } catch (error) {
        console.error(
          "Unable to load purchases:",
          error
        );

        setPageError(
          apiErrorMessage(
            error,
            "Unable to load purchase information."
          )
        );
      } finally {
        setPageLoading(false);
        setPageRefreshing(false);
      }
    },
    [user.username]
  );

  useEffect(() => {
    loadPurchaseData();
  }, [loadPurchaseData]);

  useEffect(() => {
    if (!notice) {
      return undefined;
    }

    const noticeTimer = window.setTimeout(
      () => setNotice(""),
      4000
    );

    return () => {
      window.clearTimeout(noticeTimer);
    };
  }, [notice]);

  const canCreate = Boolean(
    permissions &&
      permissions.can_create_request
  );

  const canApprove = Boolean(
    permissions &&
      permissions.can_approve_request
  );

  const canView = canCreate || canApprove;

  const statusCounts = requests.reduce(
    (counts, purchaseRequest) => {
      const status = purchaseRequest.status;

      counts[status] =
        (counts[status] || 0) + 1;

      return counts;
    },
    {}
  );

  const openRequestModal = () => {
    setRequestLines([createRequestLine()]);
    setRequestError("");
    setShowRequestModal(true);
  };

  const closeRequestModal = () => {
    if (requestSubmitting) {
      return;
    }

    setShowRequestModal(false);
    setRequestError("");
  };

  const updateRequestLine = (
    rowId,
    field,
    value
  ) => {
    setRequestLines((currentLines) =>
      currentLines.map((line) =>
        line.row_id === rowId
          ? {
              ...line,
              [field]: value,
            }
          : line
      )
    );
  };

  const addRequestLine = () => {
    setRequestLines((currentLines) => [
      ...currentLines,
      createRequestLine(),
    ]);
  };

  const removeRequestLine = (rowId) => {
    setRequestLines((currentLines) => {
      if (currentLines.length === 1) {
        return currentLines;
      }

      return currentLines.filter(
        (line) => line.row_id !== rowId
      );
    });
  };

  const submitPurchaseRequest = async (event) => {
    event.preventDefault();
    setRequestError("");

    const normalizedItems = requestLines.map(
      (line) => ({
        product_id: Number(line.product_id),
        quantity: Number(line.quantity),
      })
    );

    const invalidItem = normalizedItems.some(
      (item) =>
        !Number.isInteger(item.product_id) ||
        item.product_id <= 0 ||
        !Number.isInteger(item.quantity) ||
        item.quantity <= 0
    );

    if (invalidItem) {
      setRequestError(
        "Choose a product and enter a positive " +
          "whole-number quantity for every item."
      );
      return;
    }

    const productIds = normalizedItems.map(
      (item) => item.product_id
    );

    if (
      new Set(productIds).size !==
      productIds.length
    ) {
      setRequestError(
        "The same product cannot be added more " +
          "than once."
      );
      return;
    }

    setRequestSubmitting(true);

    try {
      const storedTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.post(
        `${API_URL}/inventory/purchases/requests/`,
        {
          items: normalizedItems,
        },
        {
          headers: {
            Authorization:
              `Bearer ${storedTokens.access}`,
          },
        }
      );

      setShowRequestModal(false);
      setNotice(
        response.data.message ||
          "Purchase request created successfully."
      );

      await loadPurchaseData(true);
    } catch (error) {
      console.error(
        "Unable to create purchase request:",
        error
      );

      setRequestError(
        apiErrorMessage(
          error,
          "Unable to create the purchase request."
        )
      );
    } finally {
      setRequestSubmitting(false);
    }
  };

  const openApprovalModal = (purchaseRequest) => {
    setApprovalRequest(purchaseRequest);
    setApprovalError("");
    setApprovalLines(
      purchaseRequest.items.map((item) => ({
        purchase_request_item_id:
          item.purchase_request_item_id,
        product_name: item.product_name,
        quantity: item.quantity,
        quantity_override: "",
        supplier_id: "",
        unit_price: "",
      }))
    );
  };

  const closeApprovalModal = () => {
    if (approvalSubmitting) {
      return;
    }

    setApprovalRequest(null);
    setApprovalLines([]);
    setApprovalError("");
  };

  const openViewModal = (purchaseRequest) => {
    setViewRequest(purchaseRequest);
  };

  const closeViewModal = () => {
    setViewRequest(null);
  };

  const fetchPurchaseHistory = async (
    purchaseRequest,
    requestedPage = 1
  ) => {
    if (
      !purchaseRequest ||
      !purchaseRequest.purchase_request_id
    ) {
      return;
    }

    const fetchId =
      latestHistoryFetchId.current + 1;
    latestHistoryFetchId.current = fetchId;

    setHistoryLoading(true);
    setHistoryError("");

    try {
      const storedTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.get(
        `${API_URL}/inventory/purchases/history/`,
        {
          headers: {
            Authorization:
              `Bearer ${storedTokens.access}`,
          },
          params: {
            purchase_request_id:
              purchaseRequest.purchase_request_id,
            page: requestedPage,
            page_size: historyPageSize,
          },
        }
      );

      if (
        fetchId !== latestHistoryFetchId.current
      ) {
        return;
      }

      const payload = response.data || {};
      const results = Array.isArray(payload.results)
        ? payload.results
        : [];

      setHistoryEvents(results);
      setHistoryCount(Number(payload.count) || 0);
      setHistoryPage(
        Number(payload.page) || requestedPage
      );
      setHistoryTotalPages(
        Number(payload.total_pages) || 0
      );
    } catch (error) {
      if (
        fetchId !== latestHistoryFetchId.current
      ) {
        return;
      }

      console.error(
        "Unable to load purchase history:",
        error
      );

      const responseData =
        error.response && error.response.data
          ? error.response.data
          : {};

      setHistoryEvents([]);
      setHistoryCount(0);
      setHistoryTotalPages(0);
      setHistoryError(
        responseData.error ||
          "Unable to load purchase history. " +
            "Please try again."
      );
    } finally {
      if (
        fetchId === latestHistoryFetchId.current
      ) {
        setHistoryLoading(false);
      }
    }
  };

  const openHistoryModal = (purchaseRequest) => {
    setHistoryRequest(purchaseRequest);
    setHistoryEvents([]);
    setHistoryError("");
    setHistoryPage(1);
    setHistoryTotalPages(0);
    setHistoryCount(0);
    setShowHistory(true);

    fetchPurchaseHistory(purchaseRequest, 1);
  };

  const closeHistoryModal = () => {
    latestHistoryFetchId.current += 1;
    setHistoryLoading(false);
    setShowHistory(false);
    setHistoryRequest(null);
    setHistoryEvents([]);
    setHistoryError("");
    setHistoryPage(1);
    setHistoryTotalPages(0);
    setHistoryCount(0);
  };

  const handleHistoryPageChange = (
    requestedPage
  ) => {
    if (
      historyLoading ||
      !historyRequest ||
      requestedPage < 1 ||
      requestedPage > historyTotalPages
    ) {
      return;
    }

    fetchPurchaseHistory(
      historyRequest,
      requestedPage
    );
  };

  const retryPurchaseHistory = () => {
    if (!historyRequest || historyLoading) {
      return;
    }

    fetchPurchaseHistory(
      historyRequest,
      historyPage
    );
  };

  const updateApprovalLine = (
    itemId,
    field,
    value
  ) => {
    setApprovalLines((currentLines) =>
      currentLines.map((line) =>
        line.purchase_request_item_id === itemId
          ? {
              ...line,
              [field]: value,
            }
          : line
      )
    );
  };

  const submitApproval = async (event) => {
    event.preventDefault();
    setApprovalError("");

    const normalizedItems = approvalLines.map(
      (line) => ({
        purchase_request_item_id:
          line.purchase_request_item_id,
        supplier_id: Number(line.supplier_id),
        unit_price: Number(line.unit_price),
        quantity:
          line.quantity_override === ""
            ? null
            : Number(line.quantity_override),
      })
    );

    const invalidItem = normalizedItems.some(
      (item) =>
        !Number.isInteger(item.supplier_id) ||
        item.supplier_id <= 0 ||
        !Number.isFinite(item.unit_price) ||
        item.unit_price < 0 ||
        (
          item.quantity !== null &&
          (
            !Number.isInteger(item.quantity) ||
            item.quantity <= 0
          )
        )
    );

    if (invalidItem) {
      setApprovalError(
        "Choose a supplier and enter a valid " +
          "USD unit price for every item."
      );
      return;
    }

    setApprovalSubmitting(true);

    try {
      const storedTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.post(
        `${API_URL}/inventory/purchases/requests/` +
          `${approvalRequest.purchase_request_id}/accept/`,
        {
          items: normalizedItems,
        },
        {
          headers: {
            Authorization:
              `Bearer ${storedTokens.access}`,
          },
        }
      );

      setApprovalRequest(null);
      setApprovalLines([]);
      setNotice(
        response.data.message ||
          "Purchase request accepted successfully."
      );

      await loadPurchaseData(true);
    } catch (error) {
      console.error(
        "Unable to accept purchase request:",
        error
      );

      setApprovalError(
        apiErrorMessage(
          error,
          "Unable to accept the purchase request."
        )
      );
    } finally {
      setApprovalSubmitting(false);
    }
  };



  const closeInspectionModal = () => {
    if (inspectionSaving) {
      return;
    }

    setInspectionRequest(null);
    setInspectionData(null);
    setInspectionLoading(false);
    setInspectionError("");
    setInspectionNotesOnly(false);
    setRefundItemId(null);
  };

  const openInspectionModal = async (
    purchaseRequest,
    notesOnly = false
  ) => {
    setInspectionRequest(purchaseRequest);
    setInspectionData(null);
    setInspectionError("");
    setInspectionNotesOnly(notesOnly);
    setRefundItemId(null);
    setInspectionLoading(true);

    try {
      const storedTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );
      const response = await axios.get(
        `${API_URL}/inventory/purchases/requests/` +
          `${purchaseRequest.purchase_request_id}/` +
          "inspection/",
        {
          headers: {
            Authorization:
              `Bearer ${storedTokens.access}`,
          },
        }
      );

      setInspectionData({
        ...response.data,
        items: (response.data.items || []).map(
          (item) => ({
            ...item,
            original_reason: item.reason || "",
          })
        ),
      });
    } catch (error) {
      console.error(
        "Unable to load purchase inspection:",
        error
      );
      setInspectionError(
        apiErrorMessage(
          error,
          "Unable to load the purchase inspection."
        )
      );
    } finally {
      setInspectionLoading(false);
    }
  };

  const updateInspectionReason = (
    receiptItemId,
    reason
  ) => {
    setInspectionData((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        items: current.items.map((item) =>
          item.goods_receipt_item_id === receiptItemId
            ? {
                ...item,
                reason,
              }
            : item
        ),
      };
    });
  };

  const saveInspectionReasons = async () => {
    if (!inspectionRequest || !inspectionData) {
      return;
    }

    const changedItems = inspectionData.items
      .map((item) => ({
        goods_receipt_item_id:
          item.goods_receipt_item_id,
        reason: (item.reason || "").trim(),
        original_reason: (
          item.original_reason || ""
        ).trim(),
      }))
      .filter(
        (item) =>
          item.reason &&
          item.reason !== item.original_reason
      )
      .map((item) => ({
        goods_receipt_item_id:
          item.goods_receipt_item_id,
        reason: item.reason,
      }));

    if (changedItems.length === 0) {
      setInspectionError(
        "Enter a new reason before saving."
      );
      return;
    }

    setInspectionSaving(true);
    setInspectionError("");

    try {
      const storedTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.patch(
        `${API_URL}/inventory/purchases/requests/` +
          `${inspectionRequest.purchase_request_id}/` +
          "inspection/",
        {
          items: changedItems,
        },
        {
          headers: {
            Authorization:
              `Bearer ${storedTokens.access}`,
          },
        }
      );

      const preparedInspection =
        response.data.inspection;

      setInspectionData({
        ...preparedInspection,
        items: (
          preparedInspection.items || []
        ).map((item) => ({
          ...item,
          original_reason:
            item.reason || "",
        })),
      });

      setNotice(
        response.data.message ||
          "Delivery reasons saved successfully."
      );

      await loadPurchaseData(true);
    } catch (error) {
      console.error(
        "Unable to save delivery reasons:",
        error
      );

      setInspectionError(
        apiErrorMessage(
          error,
          "Unable to save the delivery reasons."
        )
      );
    } finally {
      setInspectionSaving(false);
    }
  };

  const openPurchaseDocument = (
    documentRequest
  ) => {
    setViewRequest(null);
    setInspectionRequest(null);
    setInspectionData(null);
    setInspectionError("");
    setInspectionNotesOnly(false);
    setRefundItemId(null);
    setPurchaseDocument(documentRequest);
  };

  const hasInspectionReasonChanges =
    Boolean(
      inspectionData &&
        inspectionData.items.some(
          (item) => {
            const reason = (
              item.reason || ""
            ).trim();

            const originalReason = (
              item.original_reason || ""
            ).trim();

            return (
              reason &&
              reason !== originalReason
            );
          }
        )
    );

  const viewPurchaseOrders = viewRequest
    ? Object.values(
        (viewRequest.items || []).reduce(
          (groups, item) => {
            if (!item.purchase_order_id) {
              return groups;
            }

            const orderId =
              item.purchase_order_id;

            if (!groups[orderId]) {
              groups[orderId] = {
                purchase_order_id: orderId,
                status:
                  item.purchase_order_status,
                supplier_name:
                  item.supplier_name,
                items: [],
              };
            }

            groups[orderId].items.push(item);

            return groups;
          },
          {}
        )
      )
    : [];

  const openReceiptModal = (purchaseOrder) => {
    setViewRequest(null);
    setReceiptOrder(purchaseOrder);
    setReceiptWarehouseId("");
    setReceiptReference("");
    setReceiptNotes("");
    setReceiptError("");
    setReceiptIdempotencyKey(
      createReceiptIdempotencyKey()
    );
    setReceiptLines(
      purchaseOrder.items
        .filter(
          (item) =>
            Number(item.quantity_remaining) > 0
        )
        .map((item) => ({
          purchase_request_item_id:
            item.purchase_request_item_id,
          product_name: item.product_name,
          quantity_ordered: Number(
            item.quantity
          ),
          quantity_received: Number(
            item.quantity_received
          ),
          quantity_remaining: Number(
            item.quantity_remaining
          ),
          receive_now: String(
            item.quantity_remaining
          ),
        }))
    );
  };

  const closeReceiptModal = () => {
    if (receiptSubmitting) {
      return;
    }

    setReceiptOrder(null);
    setReceiptWarehouseId("");
    setReceiptReference("");
    setReceiptNotes("");
    setReceiptLines([]);
    setReceiptIdempotencyKey("");
    setReceiptError("");
  };

  const updateReceiptQuantity = (
    itemId,
    value
  ) => {
    setReceiptLines((currentLines) =>
      currentLines.map((line) =>
        line.purchase_request_item_id === itemId
          ? {
              ...line,
              receive_now: value,
            }
          : line
      )
    );
  };

  const submitGoodsReceipt = async (event) => {
    event.preventDefault();
    setReceiptError("");

    const warehouseId = Number(
      receiptWarehouseId
    );

    if (
      !Number.isInteger(warehouseId) ||
      warehouseId <= 0
    ) {
      setReceiptError(
        "Choose the warehouse receiving these items."
      );
      return;
    }

    const invalidQuantity = receiptLines.some(
      (line) => {
        const quantity = Number(
          line.receive_now
        );

        return (
          !Number.isInteger(quantity) ||
          quantity < 0 ||
          quantity >
            line.quantity_remaining
        );
      }
    );

    if (invalidQuantity) {
      setReceiptError(
        "Each received quantity must be a whole " +
          "number between zero and the remaining quantity."
      );
      return;
    }

    const submittedItems = receiptLines.map(
      (line) => ({
        purchase_request_item_id:
          line.purchase_request_item_id,
        quantity_received: Number(
          line.receive_now
        ),
      })
    );

    setReceiptSubmitting(true);

    try {
      const storedTokens = JSON.parse(
        localStorage.getItem("authTokens")
      );

      const response = await axios.post(
        `${API_URL}/inventory/purchases/orders/` +
          `${receiptOrder.purchase_order_id}/receive/`,
        {
          idempotency_key:
            receiptIdempotencyKey,
          warehouse_id: warehouseId,
          reference: receiptReference.trim(),
          notes: receiptNotes.trim(),
          items: submittedItems,
        },
        {
          headers: {
            Authorization:
              `Bearer ${storedTokens.access}`,
          },
        }
      );

      setReceiptOrder(null);
      setReceiptWarehouseId("");
      setReceiptReference("");
      setReceiptNotes("");
      setReceiptLines([]);
      setReceiptIdempotencyKey("");
      setNotice(
        response.data.message ||
          "Goods received successfully."
      );

      await loadPurchaseData(true);
    } catch (error) {
      console.error(
        "Unable to receive purchase order:",
        error
      );

      setReceiptError(
        apiErrorMessage(
          error,
          "Unable to receive the purchase order."
        )
      );
    } finally {
      setReceiptSubmitting(false);
    }
  };

  const normalizedSearch = searchInput
    .trim()
    .toLowerCase();

  const filteredRequests = normalizedSearch
    ? requests.filter((purchaseRequest) =>
        normalizedSearch
          .split(/\s+/)
          .every((word) =>
            JSON.stringify(purchaseRequest)
              .toLowerCase()
              .includes(word)
          )
      )
    : requests;

  const exportPurchaseRequestsToExcel = () => {
    const exportRows = filteredRequests.map(
      (purchaseRequest) => ({
        Request: requestNumber(
          purchaseRequest.purchase_request_id
        ),
        Created: formatDate(
          purchaseRequest.creation_date
        ),
        Requester: purchaseRequest.initiator
          ? purchaseRequest.initiator.full_name
          : "",
        Status: statusLabel(
          purchaseRequest.status
        ),
        Products: (purchaseRequest.items || [])
          .map(
            (item) =>
              `${item.product_name} x ${item.quantity}`
          )
          .join(", "),
        Orders: [
          ...new Set(
            (purchaseRequest.items || [])
              .map(
                (item) =>
                  item.purchase_order_id
                    ? purchaseOrderNumber(
                        item.purchase_order_id
                      )
                    : ""
              )
              .filter(Boolean)
          ),
        ].join(", "),
        Suppliers: [
          ...new Set(
            (purchaseRequest.items || [])
              .map(
                (item) =>
                  item.supplier_name || ""
              )
              .filter(Boolean)
          ),
        ].join(", "),
        "Unit Prices": (
          purchaseRequest.items || []
        )
          .filter(
            (item) =>
              item.unit_price !== null &&
              item.unit_price !== undefined
          )
          .map(
            (item) =>
              `${item.product_name}: ` +
              `${formatMoney(item.unit_price)}`
          )
          .join(", "),
        "Approved By": purchaseRequest.receiver
          ? purchaseRequest.receiver.full_name
          : "",
        "Total USD":
          Number(purchaseRequest.total_amount) > 0
            ? Number(
                purchaseRequest.total_amount
              ).toFixed(2)
            : "",
      })
    );

    const worksheet =
      XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "Purchase Requests"
    );

    XLSX.writeFile(
      workbook,
      "Purchase Requests.xlsx"
    );
  };

  const PurchaseToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />

      <Button
        variant="link"
        onClick={exportPurchaseRequestsToExcel}
      >
        <img
          src={ExcelSVG}
          alt="Excel Icon"
          style={{
            width: "20px",
            marginRight: "8px",
          }}
        />
        Excel
      </Button>
    </GridToolbarContainer>
  );

  const hasCreatedOrders = requests.some(
    (purchaseRequest) =>
      (purchaseRequest.items || []).some(
        (item) => Boolean(item.purchase_order_id)
      )
  );

  const requestColumns = [
    {
      field: "purchase_request_id",
      headerName: inventoryText("inventory.Request"),
      minWidth: 130,
      flex: 1,
      valueFormatter: (params) =>
        requestNumber(params.value),
    },
    {
      field: "creation_date",
      headerName: inventoryText("inventory.Created"),
      minWidth: 170,
      flex: 1,
      valueFormatter: (params) =>
        formatDate(params.value),
    },
    {
      field: "requester",
      headerName: inventoryText("inventory.Requester"),
      minWidth: 150,
      flex: 1,
      valueGetter: (params) =>
        params.row.initiator
          ? params.row.initiator.full_name
          : "—",
    },
    {
      field: "status",
      headerName: inventoryText("inventory.Status"),
      minWidth: 110,
      flex: 1,
      valueFormatter: (params) =>
        statusLabel(params.value),
    },
    {
      field: "request_items",
      headerName: inventoryText("inventory.Products"),
      minWidth: 260,
      flex: 2,
      sortable: false,
      valueGetter: (params) =>
        (params.row.items || [])
          .map(
            (item) =>
              `${item.product_name} x ${item.quantity}`
          )
          .join(", "),
    },
    ...(hasCreatedOrders
      ? [
          {
            field: "purchase_orders",
            headerName: inventoryText("inventory.Orders"),
            minWidth: 150,
            flex: 1,
            sortable: false,
            valueGetter: (params) => {
              const orderIds = Array.from(
                new Set(
                  (params.row.items || [])
                    .map(
                      (item) =>
                        item.purchase_order_id
                    )
                    .filter(Boolean)
                )
              );

              return orderIds.length
                ? orderIds
                    .map(purchaseOrderNumber)
                    .join(", ")
                : "—";
            },
          },
          {
            field: "suppliers",
            headerName: inventoryText("inventory.Suppliers"),
            minWidth: 150,
            flex: 1,
            sortable: false,
            valueGetter: (params) => {
              const supplierNames = Array.from(
                new Set(
                  (params.row.items || [])
                    .map(
                      (item) =>
                        item.supplier_name
                    )
                    .filter(Boolean)
                )
              );

              return supplierNames.length
                ? supplierNames.join(", ")
                : "—";
            },
          },
          {
            field: "unit_prices",
            headerName: inventoryText("inventory.UnitPrices"),
            minWidth: 220,
            flex: 1,
            sortable: false,
            valueGetter: (params) => {
              const prices = (
                params.row.items || []
              )
                .filter(
                  (item) =>
                    item.unit_price !== null &&
                    item.unit_price !== undefined
                )
                .map(
                  (item) =>
                    `${item.product_name}: ` +
                    `${formatMoney(item.unit_price)}`
                );

              return prices.length
                ? prices.join(", ")
                : "—";
            },
          },
        ]
      : []),
    {
      field: "approved_by",
      headerName: inventoryText("inventory.ApprovedBy"),
      minWidth: 150,
      flex: 1,
      valueGetter: (params) =>
        params.row.receiver
          ? params.row.receiver.full_name
          : "—",
    },
    {
      field: "total_amount",
      headerName: inventoryText("inventory.TotalUsd"),
      minWidth: 110,
      flex: 1,
      valueFormatter: (params) =>
        Number(params.value) > 0
          ? formatMoney(params.value)
          : "—",
    },
    {
      field: "actions",
      headerName: inventoryText("inventory.Actions"),
      minWidth: 385,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="supplier-row-actions">
          {params.row.can_accept ? (
            <button
              type="button"
              className="supplier-row-action"
              onClick={() =>
                openApprovalModal(params.row)
              }
            >
              Accept
            </button>
          ) : (params.row.items || []).some(
              (item) =>
                Boolean(item.purchase_order_id)
            ) ? (
            <button
              type="button"
              className="supplier-row-action"
              onClick={() =>
                openViewModal(params.row)
              }
            >
              {inventoryText("inventory.View")}
            </button>
          ) : null}

          <button
            type="button"
            className="supplier-row-action"
            onClick={() =>
              openHistoryModal(params.row)
            }
          >{inventoryText("inventory.History")}</button>

          {params.row.has_inspection && (
            <button
              type="button"
              className="supplier-row-action"
              onClick={() =>
                openInspectionModal(params.row)
              }
            >
              {inventoryText("inventory.Inspect")}
            </button>
          )}

          {params.row.has_delivery_notes && (
            <button
              type="button"
              className="supplier-row-action"
              onClick={() =>
                openInspectionModal(params.row, true)
              }
            >
              {inventoryText("inventory.Notes")}
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="purchase-page">
      <div className="purchase-page-toolbar">
        <div className="purchase-page-actions">
          {canCreate && (
            <button
              type="button"
              className="AddClient purchase-top-action"
              onClick={openRequestModal}
            >
              {inventoryText("inventory.RequestAnOrder")}
            </button>
          )}

          <NavLink
            exact
            to="/inventory"
            className="AddClient purchase-top-action"
          >{inventoryText("inventory.GoBack")}</NavLink>

          {canView && (
            <button
              type="button"
              className="AddClient purchase-top-action"
              disabled={pageRefreshing}
              onClick={() =>
                loadPurchaseData(true)
              }
            >
              {pageRefreshing
                ? "Refreshing..."
                : "Refresh"}
            </button>
          )}
        </div>

      </div>

      {notice && (
        <div
          className="purchase-notice"
          role="status"
        >
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice("")}
            aria-label="Dismiss message"
          >
            ×
          </button>
        </div>
      )}

      {pageLoading ? (
        <div className="purchase-state">
          <span
            className="purchase-spinner"
            aria-hidden="true"
          />
          <h2>Loading purchases</h2>
          <p>
            Checking your permissions and purchase
            requests.
          </p>
        </div>
      ) : pageError ? (
        <div className="purchase-state purchase-state-error">
          <h2>Purchases could not be loaded</h2>
          <p>{pageError}</p>
          <button
            type="button"
            className="purchase-button purchase-button-secondary"
            onClick={() => loadPurchaseData()}
          >{inventoryText("inventory.TryAgain")}</button>
        </div>
      ) : !canView ? (
        <div className="purchase-state">
          <h2>Purchase access is not enabled</h2>
          <p>
            This account does not have the required
            employee and accounting permissions.
          </p>
        </div>
      ) : (
        <>
          <div className="purchase-summary-grid">
              <div className="purchase-summary-card">
                <span>{inventoryText("inventory.TotalRequests")}</span>
                <strong>{requests.length}</strong>
              </div>

              <div className="purchase-summary-card">
                <span>{inventoryText("inventory.Pending")}</span>
                <strong>
                  {(statusCounts.pending || 0) +
                    (statusCounts.partially_delivered || 0)}
                </strong>
              </div>

              <div className="purchase-summary-card">
                <span>{inventoryText("inventory.Delivered")}</span>
                <strong>
                  {statusCounts.delivered || 0}
                </strong>
              </div>
            </div>

            <div className="ClientTable supplier-table">
              <style>{`
                .MuiDataGrid-panelWrapper {
                  background: #122632 !important;
                }
              `}</style>

              <input
                type="text"
                placeholder={inventoryText("inventory.Search")}
                value={searchInput}
                onChange={(event) =>
                  setSearchInput(event.target.value)
                }
                className="search-client"
              />

              <div
                style={{
                  height: 520,
                  width: "100%",
                }}
              >
                <DataGrid
                  rows={filteredRequests}
                  columns={requestColumns}
                  getRowId={(row) =>
                    row.purchase_request_id
                  }
                  loading={pageRefreshing}
                  components={{
                    Toolbar: PurchaseToolbar,
                  }}
                  rowsPerPageOptions={[
                    10,
                    100,
                    1000,
                  ]}
                  pagination
                  disableSelectionOnClick
                  localeText={{
                    noRowsLabel:
                      "No purchase requests found.",
                    noResultsOverlayLabel:
                      "No matching purchase requests found.",
                  }}
                />
              </div>
            </div>
        </>
      )}

      <Modal
        show={showHistory}
        onHide={closeHistoryModal}
        className="supplier-history-modal supplier-themed-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Purchase History
            {historyRequest
              ? ` — ${requestNumber(
                  historyRequest.purchase_request_id
                )}`
              : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {historyLoading ? (
            <div
              className="supplier-history-state supplier-history-loading"
              role="status"
              aria-live="polite"
            >
              <span
                className="supplier-history-spinner"
                aria-hidden="true"
              />
              <h3>Loading purchase history</h3>
              <p>
                Retrieving recorded purchase and inventory activity.
              </p>
            </div>
          ) : historyError ? (
            <div
              className="supplier-history-state supplier-history-error"
              role="alert"
            >
              <h3>{inventoryText("inventory.HistoryCouldNotBeLoaded")}</h3>
              <p>{historyError}</p>
              <button
                type="button"
                className="supplier-modal-secondary"
                onClick={retryPurchaseHistory}
              >{inventoryText("inventory.TryAgain")}</button>
            </div>
          ) : historyEvents.length === 0 ? (
            <div className="supplier-history-state supplier-history-empty">
              <h3>No purchase history yet</h3>
              <p>
                There is no recorded purchase or inventory activity for{" "}
                <strong>
                  {historyRequest
                    ? requestNumber(
                        historyRequest.purchase_request_id
                      )
                    : "this purchase request"}
                </strong>
                .
              </p>
            </div>
          ) : (
            <>
              <div className="supplier-history-summary">
                <span>
                  {historyCount}{" "}
                  {historyCount === 1
                    ? "recorded event"
                    : "recorded events"}
                </span>
                <span>{inventoryText("inventory.NewestActivityFirst")}</span>
              </div>

              <ol className="supplier-history-timeline">
                {historyEvents.map((event) => {
                  const metaItems =
                    getPurchaseHistoryMeta(event);

                  return (
                    <li
                      key={
                        event.inventory_audit_event_id
                      }
                      className="supplier-history-event"
                    >
                      <span
                        className="supplier-history-marker"
                        aria-hidden="true"
                      />

                      <article className="supplier-history-card">
                        <div className="supplier-history-card-header">
                          <span className="supplier-history-event-label">
                            {event.event_label}
                          </span>
                          <time
                            dateTime={event.creation_date}
                            className="supplier-history-date"
                          >
                            {formatPurchaseHistoryDate(
                              event.creation_date
                            )}
                          </time>
                        </div>

                        <p className="supplier-history-description">
                          {event.description}
                        </p>

                        {metaItems.length > 0 && (
                          <dl className="supplier-history-meta">
                            {metaItems.map(
                              (item, index) => (
                                <div
                                  key={
                                    `${item.label}-${index}`
                                  }
                                  className="supplier-history-meta-item"
                                >
                                  <dt>{item.label}</dt>
                                  <dd>{item.value}</dd>
                                </div>
                              )
                            )}
                          </dl>
                        )}
                      </article>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="supplier-history-pagination">
            {historyTotalPages > 1 && (
              <>
                <button
                  type="button"
                  className="supplier-modal-secondary"
                  disabled={
                    historyLoading ||
                    historyPage <= 1
                  }
                  onClick={() =>
                    handleHistoryPageChange(
                      historyPage - 1
                    )
                  }
                >{inventoryText("inventory.Previous")}</button>

                <span>
                  Page {historyPage} of{" "}
                  {historyTotalPages}
                </span>

                <button
                  type="button"
                  className="supplier-modal-secondary"
                  disabled={
                    historyLoading ||
                    historyPage >= historyTotalPages
                  }
                  onClick={() =>
                    handleHistoryPageChange(
                      historyPage + 1
                    )
                  }
                >{inventoryText("inventory.Next")}</button>
              </>
            )}
          </div>

          <button
            type="button"
            className="supplier-modal-secondary"
            onClick={closeHistoryModal}
          >{inventoryText("inventory.Close")}</button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRequestModal}
        onHide={closeRequestModal}
        className="purchase-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {inventoryText("inventory.RequestAnOrder")}
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={submitPurchaseRequest}>
          <Modal.Body>
            <p className="purchase-modal-intro">
              Select the products and quantities needed.
              The creation date and requester are recorded
              automatically.
            </p>

            {requestError && (
              <div
                className="purchase-form-error"
                role="alert"
              >
                {requestError}
              </div>
            )}

            <div className="purchase-form-lines">
              {requestLines.map(
                (line, index) => (
                  <div
                    className="purchase-form-line"
                    key={line.row_id}
                  >
                    <div className="purchase-form-line-number">
                      {index + 1}
                    </div>

                    <label>{inventoryText("inventory.Product")}<select
                        value={line.product_id}
                        onChange={(event) =>
                          updateRequestLine(
                            line.row_id,
                            "product_id",
                            event.target.value
                          )
                        }
                        required
                      >
                        <option value="">{inventoryText("inventory.ChooseProduct")}</option>
                        {products.map((product) => (
                          <option
                            key={product.product_id}
                            value={product.product_id}
                          >
                            {product.name}
                            {product.code
                              ? ` — ${product.code}`
                              : ""}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label>{inventoryText("inventory.Quantity")}<input
                        type="number"
                        min="1"
                        step="1"
                        value={line.quantity}
                        onChange={(event) =>
                          updateRequestLine(
                            line.row_id,
                            "quantity",
                            event.target.value
                          )
                        }
                        required
                      />
                    </label>

                    <button
                      type="button"
                      className="purchase-remove-line"
                      disabled={
                        requestLines.length === 1
                      }
                      onClick={() =>
                        removeRequestLine(
                          line.row_id
                        )
                      }
                    >{inventoryText("inventory.Remove")}</button>
                  </div>
                )
              )}
            </div>

            <button
              type="button"
              className="purchase-add-line"
              onClick={addRequestLine}
            >
              + Add Another Product
            </button>
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="purchase-button purchase-button-secondary"
              disabled={requestSubmitting}
              onClick={closeRequestModal}
            >{inventoryText("inventory.Cancel")}</button>
            <button
              type="submit"
              className="purchase-button purchase-button-primary"
              disabled={
                requestSubmitting ||
                products.length === 0
              }
            >
              {requestSubmitting
                ? "Creating..."
                : "Create Request"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={Boolean(viewRequest)}
        onHide={closeViewModal}
        className="purchase-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Purchase Details
            {viewRequest
              ? ` — ${requestNumber(
                  viewRequest.purchase_request_id
                )}`
              : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {viewRequest && (
            <>
              <p className="purchase-modal-intro">
                Status:{" "}
                <strong>
                  {statusLabel(viewRequest.status)}
                </strong>
                {" · "}
                Approved by:{" "}
                <strong>
                  {viewRequest.receiver
                    ? viewRequest.receiver.full_name
                    : "—"}
                </strong>
                {" · "}
                Total:{" "}
                <strong>
                  {Number(
                    viewRequest.total_amount
                  ) > 0
                    ? formatMoney(
                        viewRequest.total_amount
                      )
                    : "—"}
                </strong>
              </p>

              {viewPurchaseOrders.length === 0 ? (
                <p className="purchase-modal-intro">
                  No purchase orders have been created
                  for this request.
                </p>
              ) : (
                <div className="purchase-approval-lines">
                  {viewPurchaseOrders.map(
                    (purchaseOrder) => {
                      const canReceiveOrder =
                        canApprove &&
                        [
                          "pending",
                          "partially_delivered",
                        ].includes(
                          purchaseOrder.status
                        ) &&
                        purchaseOrder.items.some(
                          (item) =>
                            Number(
                              item.quantity_remaining
                            ) > 0
                        );

                      const orderedQuantity =
                        purchaseOrder.items.reduce(
                          (total, item) =>
                            total +
                            Number(item.quantity || 0),
                          0
                        );

                      const receivedQuantity =
                        purchaseOrder.items.reduce(
                          (total, item) =>
                            total +
                            Number(
                              item.quantity_received || 0
                            ),
                          0
                        );

                      const orderTotal =
                        purchaseOrder.items.reduce(
                          (total, item) =>
                            total +
                            Number(
                              item.line_total || 0
                            ),
                          0
                        );

                      return (
                        <div
                          className="purchase-approval-line"
                          key={
                            purchaseOrder
                              .purchase_order_id
                          }
                        >
                          <div className="purchase-approval-product">
                            <strong>
                              {purchaseOrderNumber(
                                purchaseOrder
                                  .purchase_order_id
                              )}
                            </strong>
                            <span>
                              {purchaseOrder
                                .supplier_name || "—"}
                              {" · "}
                              {statusLabel(
                                purchaseOrder.status
                              )}
                            </span>
                          </div>

                          <label>{inventoryText("inventory.Products")}<input
                              type="text"
                              readOnly
                              value={purchaseOrder.items
                                .map(
                                  (item) =>
                                    `${item.product_name} ` +
                                    `x ${item.quantity}`
                                )
                                .join(", ")}
                            />
                          </label>

                          <label>
                            {inventoryText("inventory.UnitPrices")}
                            <input
                              type="text"
                              readOnly
                              value={purchaseOrder.items
                                .map(
                                  (item) =>
                                    `${item.product_name}: ` +
                                    `${formatMoney(
                                      item.unit_price
                                    )}`
                                )
                                .join(", ")}
                            />
                          </label>

                          <label>
                            {inventoryText("inventory.Received")}
                            <input
                              type="text"
                              readOnly
                              value={
                                `${receivedQuantity} / ` +
                                `${orderedQuantity}`
                              }
                            />
                          </label>

                          <div className="purchase-line-total">
                            <span>{inventoryText("inventory.OrderTotal")}</span>
                            <strong>
                              {formatMoney(orderTotal)}
                            </strong>
                          </div>

                          {canReceiveOrder && (
                            <button
                              type="button"
                              className="edit-client"
                              onClick={() =>
                                openReceiptModal(
                                  purchaseOrder
                                )
                              }
                            >
                              {inventoryText("inventory.ReceiveItems")}
                            </button>
                          )}

                          <button
                            type="button"
                            className="edit-client"
                            onClick={() =>
                              openPurchaseDocument({
                                kind: "order",
                                id:
                                  purchaseOrder
                                    .purchase_order_id,
                                label:
                                  purchaseOrderNumber(
                                    purchaseOrder
                                      .purchase_order_id
                                  ),
                              })
                            }
                          >
                            {inventoryText("inventory.Receipts")}
                          </button>
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="purchase-button purchase-button-secondary"
            onClick={closeViewModal}
          >{inventoryText("inventory.Close")}</button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={Boolean(receiptOrder)}
        onHide={closeReceiptModal}
        className="purchase-modal"
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {inventoryText("inventory.ReceiveItems")}
            {receiptOrder
              ? ` — ${purchaseOrderNumber(
                  receiptOrder.purchase_order_id
                )}`
              : ""}
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={submitGoodsReceipt}>
          <Modal.Body>
            <p className="purchase-modal-intro">
              Record the warehouse and quantities that
              physically arrived. Inventory will increase
              only after confirmation.
            </p>

            {receiptError && (
              <div
                className="purchase-form-error"
                role="alert"
              >
                {receiptError}
              </div>
            )}

            <div className="purchase-approval-lines">
              <div className="purchase-approval-line">
                <label>{inventoryText("inventory.Warehouse")}<select
                    value={receiptWarehouseId}
                    onChange={(event) =>
                      setReceiptWarehouseId(
                        event.target.value
                      )
                    }
                    required
                  >
                    <option value="">{inventoryText("inventory.ChooseWarehouse")}</option>

                    {warehouses.map((warehouse) => (
                      <option
                        key={warehouse.warehouse_id}
                        value={warehouse.warehouse_id}
                      >
                        {warehouse.warehouse_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Delivery Reference
                  <input
                    type="text"
                    maxLength="255"
                    value={receiptReference}
                    onChange={(event) =>
                      setReceiptReference(
                        event.target.value
                      )
                    }
                    placeholder="Optional"
                  />
                </label>

                <label>
                  {inventoryText("inventory.Notes")}
                  <input
                    type="text"
                    maxLength="5000"
                    value={receiptNotes}
                    onChange={(event) =>
                      setReceiptNotes(
                        event.target.value
                      )
                    }
                    placeholder="Optional"
                  />
                </label>
              </div>

              {receiptLines.map((line) => {
                const receiveNow = Number(
                  line.receive_now || 0
                );

                return (
                  <div
                    className="purchase-approval-line"
                    key={
                      line.purchase_request_item_id
                    }
                  >
                    <div className="purchase-approval-product">
                      <strong>
                        {line.product_name}
                      </strong>
                      <span>
                        Ordered:{" "}
                        {line.quantity_ordered}
                        {" · "}
                        Previously received:{" "}
                        {line.quantity_received}
                        {" · "}
                        Remaining:{" "}
                        {line.quantity_remaining}
                      </span>
                    </div>

                    <label>
                      Receive Now
                      <input
                        type="number"
                        min="0"
                        max={line.quantity_remaining}
                        step="1"
                        value={line.receive_now}
                        onChange={(event) =>
                          updateReceiptQuantity(
                            line
                              .purchase_request_item_id,
                            event.target.value
                          )
                        }
                        required
                      />
                    </label>

                    <div className="purchase-line-total">
                      <span>Remaining After</span>
                      <strong>
                        {Math.max(
                          line.quantity_remaining -
                            (Number.isFinite(
                              receiveNow
                            )
                              ? receiveNow
                              : 0),
                          0
                        )}
                      </strong>
                    </div>
                  </div>
                );
              })}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="purchase-button purchase-button-secondary"
              disabled={receiptSubmitting}
              onClick={closeReceiptModal}
            >{inventoryText("inventory.Cancel")}</button>

            <button
              type="submit"
              className="purchase-button purchase-button-primary"
              disabled={
                receiptSubmitting ||
                warehouses.length === 0 ||
                receiptLines.length === 0
              }
            >
              {receiptSubmitting
                ? "Receiving..."
                : "Confirm"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal
        show={Boolean(inspectionRequest)}
        onHide={closeInspectionModal}
        className={
          "purchase-modal " +
          "purchase-inspection-modal"
        }
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {inspectionNotesOnly
              ? "Delivery Notes"
              : "Purchase Inspection"}
            {inspectionRequest
              ? ` — ${requestNumber(
                  inspectionRequest.purchase_request_id
                )}`
              : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {inspectionLoading ? (
            <div className="purchase-inspection-state">
              Loading inspection…
            </div>
          ) : inspectionError && !inspectionData ? (
            <div
              className="purchase-form-error"
              role="alert"
            >
              {inspectionError}
            </div>
          ) : inspectionData ? (
            <>
              {inspectionError && (
                <div
                  className="purchase-form-error"
                  role="alert"
                >
                  {inspectionError}
                </div>
              )}

              <div className="purchase-inspection-list">
                {inspectionData.items
                  .filter(
                    (item) =>
                      !inspectionNotesOnly ||
                      Boolean(
                        (item.reason || "").trim()
                      ) ||
                      Boolean(
                        (item.receipt_notes || "").trim()
                      )
                  )
                  .map((item) => (
                    <article
                      className="purchase-inspection-item"
                      key={item.goods_receipt_item_id}
                    >
                      <div className="purchase-inspection-heading">
                        <div>
                          <strong>{item.product_name}</strong>
                          <span>
                            {item.receipt_number}
                            {" · "}
                            {item.order_number}
                          </span>
                        </div>
                        <span
                          className={
                            "purchase-inspection-status " +
                            `purchase-inspection-status-${item.status}`
                          }
                        >
                          {item.status_label}
                        </span>
                      </div>

                      <dl className="purchase-inspection-facts">
                        <div>
                          <dt>{inventoryText("inventory.Supplier")}</dt>
                          <dd>{item.supplier_name}</dd>
                        </div>
                        <div>
                          <dt>{inventoryText("inventory.Warehouse")}</dt>
                          <dd>{item.warehouse_name}</dd>
                        </div>
                        <div>
                          <dt>Ordered</dt>
                          <dd>{item.quantity_ordered}</dd>
                        </div>
                        <div>
                          <dt>Received This Time</dt>
                          <dd>
                            {item.quantity_received_now}
                          </dd>
                        </div>
                        <div>
                          <dt>Received Total</dt>
                          <dd>
                            {item.quantity_received_total}
                          </dd>
                        </div>
                        <div>
                          <dt>Refunded</dt>
                          <dd>
                            {item.quantity_refunded || 0}
                          </dd>
                        </div>
                        <div>
                          <dt>Refundable</dt>
                          <dd>
                            {item.quantity_refundable || 0}
                          </dd>
                        </div>
                        <div>
                          <dt>Delivery Reference</dt>
                          <dd>{item.reference || "—"}</dd>
                        </div>
                      </dl>

                      {item.receipt_notes && (
                        <div className="purchase-inspection-receipt-note">
                          <strong>Receipt Notes</strong>
                          <p>{item.receipt_notes}</p>
                        </div>
                      )}

                      {inspectionNotesOnly ? (
                        <div
                          className={
                            "purchase-inspection-readonly-note"
                          }
                        >
                          <strong>{inventoryText("inventory.Reason")}</strong>
                          <p>{item.reason || "—"}</p>
                        </div>
                      ) : (
                        <label
                          className={
                            "purchase-inspection-reason"
                          }
                        >{inventoryText("inventory.Reason")}<textarea
                            rows="3"
                            maxLength="5000"
                            value={item.reason || ""}
                            disabled={
                              !inspectionData.can_edit ||
                              inspectionSaving
                            }
                            placeholder={
                              "Enter a reason to save"
                            }
                            onChange={(event) =>
                              updateInspectionReason(
                                item.goods_receipt_item_id,
                                event.target.value
                              )
                            }
                          />
                        </label>
                      )}

                      {!inspectionNotesOnly && (
                        <div
                          className={
                            "purchase-inspection-actions"
                          }
                        >
                          <button
                            type="button"
                            className="edit-client"
                            onClick={() =>
                              openPurchaseDocument({
                                kind: "delivery",
                                id:
                                  item.goods_receipt_id,
                                label:
                                  item.receipt_number,
                              })
                            }
                          >
                            Receipt
                          </button>

                          {(item.refunds || []).map(
                            (refund) => (
                              <button
                                type="button"
                                className={
                                  "supplier-row-action"
                                }
                                key={
                                  refund
                                    .purchase_refund_id
                                }
                                onClick={() =>
                                  openPurchaseDocument({
                                    kind: "refund",
                                    id:
                                      refund
                                        .purchase_refund_id,
                                    label:
                                      refund
                                        .refund_number,
                                  })
                                }
                              >
                                {
                                  refund
                                    .refund_number
                                }
                              </button>
                            )
                          )}

                          {inspectionData.can_refund &&
                            Number(
                              item
                                .quantity_refundable ||
                                0
                            ) > 0 && (
                              <button
                                type="button"
                                className={
                                  "supplier-row-action " +
                                  "purchase-refund-open"
                                }
                                onClick={() =>
                                  setRefundItemId(
                                    item
                                      .goods_receipt_item_id
                                  )
                                }
                              >
                                Refund
                              </button>
                            )}
                        </div>
                      )}

                      {!inspectionNotesOnly &&
                        refundItemId ===
                          item
                            .goods_receipt_item_id && (
                          <PurchaseRefundPanel
                            item={item}
                            apiUrl={API_URL}
                            onCancel={() =>
                              setRefundItemId(
                                null
                              )
                            }
                            onCompleted={(
                              refund
                            ) => {
                              setNotice(
                                `${
                                  refund
                                    .refund_number
                                } ` +
                                  "processed " +
                                  "successfully."
                              );

                              setRefundItemId(
                                null
                              );

                              openInspectionModal(
                                inspectionRequest
                              );

                              loadPurchaseData(
                                true
                              );
                            }}
                          />
                        )}
                    </article>
                  ))}

                {inspectionNotesOnly &&
                  !inspectionData.items.some(
                    (item) =>
                      Boolean(
                        (item.reason || "").trim()
                      ) ||
                      Boolean(
                        (item.receipt_notes || "").trim()
                      )
                  ) && (
                    <div className="purchase-inspection-state">
                      No delivery notes have been recorded.
                    </div>
                  )}
              </div>
            </>
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className={
              "purchase-button " +
              "purchase-button-secondary"
            }
            disabled={inspectionSaving}
            onClick={closeInspectionModal}
          >{inventoryText("inventory.Close")}</button>

          {inspectionData &&
            inspectionData.can_edit &&
            !inspectionNotesOnly && (
            <button
              type="button"
              className={
                "purchase-button " +
                "purchase-button-primary"
              }
              disabled={
                inspectionSaving ||
                !hasInspectionReasonChanges
              }
              onClick={saveInspectionReasons}
            >
              {inspectionSaving
                ? "Saving..."
                : "Save Reasons"}
            </button>
          )}
        </Modal.Footer>
      </Modal>

      <PurchaseDocumentModal
        request={purchaseDocument}
        apiUrl={API_URL}
        onClose={() =>
          setPurchaseDocument(null)
        }
      />

      <Modal
        show={Boolean(approvalRequest)}
        onHide={closeApprovalModal}
        className={
          "purchase-modal " +
          "purchase-acceptance-modal"
        }
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Accept{" "}
            {approvalRequest
              ? requestNumber(
                  approvalRequest
                    .purchase_request_id
                )
              : "Purchase Request"}
          </Modal.Title>
        </Modal.Header>

        <form onSubmit={submitApproval}>
          <Modal.Body>
            <p className="purchase-modal-intro">
              Assign a supplier and USD unit price to
              every product. Supplier balances will be
              increased immediately after confirmation.
            </p>

            {approvalError && (
              <div
                className="purchase-form-error"
                role="alert"
              >
                {approvalError}
              </div>
            )}

            <div className="purchase-approval-lines">
              {approvalLines.map((line) => (
                <div
                  className={
                    "purchase-approval-line " +
                    "purchase-acceptance-line"
                  }
                  key={
                    line.purchase_request_item_id
                  }
                >
                  <div className="purchase-approval-product">
                    <strong>
                      {line.product_name}
                    </strong>
                    <span>
                      Requested: {line.quantity}
                    </span>
                  </div>

                  <label>
                    Final Quantity (Optional)
                    <input
                      type="number"
                      min="1"
                      max="2147483647"
                      step="1"
                      value={line.quantity_override}
                      placeholder={`Keep ${line.quantity}`}
                      onChange={(event) =>
                        updateApprovalLine(
                          line
                            .purchase_request_item_id,
                          "quantity_override",
                          event.target.value
                        )
                      }
                    />
                  </label>

                  <label>{inventoryText("inventory.Supplier")}<select
                      value={line.supplier_id}
                      onChange={(event) =>
                        updateApprovalLine(
                          line
                            .purchase_request_item_id,
                          "supplier_id",
                          event.target.value
                        )
                      }
                      required
                    >
                      <option value="">{inventoryText("inventory.ChooseSupplier")}</option>
                      {suppliers.map((supplier) => (
                        <option
                          key={supplier.supplier_id}
                          value={supplier.supplier_id}
                        >
                          {supplier.company_name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    Unit Price (USD)
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unit_price}
                      onChange={(event) =>
                        updateApprovalLine(
                          line
                            .purchase_request_item_id,
                          "unit_price",
                          event.target.value
                        )
                      }
                      required
                    />
                  </label>

                  <div className="purchase-line-total">
                    <span>Line Total</span>
                    <strong>
                      {line.unit_price !== "" &&
                      Number.isFinite(
                        Number(line.unit_price)
                      )
                        ? formatMoney(
                            Number(line.unit_price) *
                              Number(
                                line.quantity_override === ""
                                  ? line.quantity
                                  : line.quantity_override
                              )
                          )
                        : "—"}
                    </strong>
                  </div>
                </div>
              ))}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <button
              type="button"
              className="purchase-button purchase-button-secondary"
              disabled={approvalSubmitting}
              onClick={closeApprovalModal}
            >{inventoryText("inventory.Cancel")}</button>
            <button
              type="submit"
              className="purchase-button purchase-button-primary"
              disabled={
                approvalSubmitting ||
                suppliers.length === 0
              }
            >
              {approvalSubmitting
                ? "Accepting..."
                : "Confirm and Accept"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}

export default Purchases;
